import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Container,
  Chip,
  Button,
  Paper,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  AutoAwesome as AutoAwesomeIcon,
  AccountBalance as AccountBalanceIcon,
  Handshake as HandshakeIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as industryApi from '../services/industry';
import type { Industry } from '../services/industry';
import logoLight from '../assets/logo-black.png';
import ValueChainTable from '../components/valueChain/ValueChainTable';

// ============================================
// COLOR CONSTANTS
// ============================================
const COLORS = {
  // Brand Colors
  primaryBlue: '#00A7E1',
  primaryGreen: '#00D4AA',
  accentLime: '#A4E63C',

  // Neutrals
  white: '#FFFFFF',
  gray50: '#F8F9FA',
  gray100: '#F1F3F5',
  gray200: '#E9ECEF',
  gray300: '#DEE2E6',
  gray400: '#CED4DA',
  gray500: '#ADB5BD',
  gray600: '#6C757D',
  gray700: '#495057',
  gray800: '#343A40',
  gray900: '#212529',

  // Semantic
  success: '#4CAF50',
  successLight: '#E8F5E9',
  warning: '#FF9800',
  warningLight: '#FFF3E0',
  error: '#F44336',
  errorLight: '#FFEBEE',
  info: '#2196F3',
  infoLight: '#E3F2FD',

  // Gradients
  brandGradient: 'linear-gradient(135deg, #00A7E1 0%, #00D4AA 50%, #A4E63C 100%)',
  brandGradientHorizontal: 'linear-gradient(90deg, #00A7E1 0%, #00D4AA 50%, #A4E63C 100%)'
};

// ============================================
// COVER PAGE COMPONENT
// ============================================
interface CoverPageProps {
  industry: Industry;
  reportDate: Date;
}

const CoverPage: React.FC<CoverPageProps> = ({ industry, reportDate }) => (
  <Box
    className="pdf-page cover-page"
    sx={{
      width: '210mm',
      minHeight: '297mm',
      background: COLORS.brandGradient,
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      padding: '40px',
      color: 'white',
      boxSizing: 'border-box',
      pageBreakAfter: 'always',
      '@media print': {
        pageBreakAfter: 'always'
      }
    }}
  >
    {/* Decorative Background Pattern */}
    <Box
      sx={{
        position: 'absolute',
        top: '-50%',
        right: '-20%',
        width: '80%',
        height: '150%',
        opacity: 0.08,
        background: 'radial-gradient(circle, white 2px, transparent 2px)',
        backgroundSize: '40px 40px',
        transform: 'rotate(-15deg)'
      }}
    />

    {/* Header Section */}
    <Box sx={{ position: 'relative', zIndex: 1 }}>
      <img
        src={logoLight}
        alt="VALUE CHAIN GLOBAL"
        style={{
          height: '60px',
          marginBottom: '32px',
          filter: 'brightness(0) invert(1)'
        }}
      />
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '2px',
          textTransform: 'uppercase',
          opacity: 0.9
        }}
      >
        Industry Intelligence Report
      </Typography>
      <Box
        sx={{
          width: '80px',
          height: '3px',
          background: 'white',
          opacity: 0.5,
          marginTop: '12px'
        }}
      />
    </Box>

    {/* Main Content - Industry Name & Metrics */}
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        position: 'relative',
        zIndex: 1,
        maxWidth: '75%'
      }}
    >
      {/* Industry Name */}
      <Typography
        sx={{
          fontSize: '72px',
          fontWeight: 800,
          lineHeight: 1.1,
          marginBottom: '24px',
          textShadow: '0 4px 30px rgba(0,0,0,0.2)'
        }}
      >
        {industry.name}
      </Typography>

      {/* Subtitle */}
      <Typography
        sx={{
          fontSize: '28px',
          fontWeight: 300,
          lineHeight: 1.4,
          opacity: 0.95,
          marginBottom: '48px'
        }}
      >
        Comprehensive Market Analysis & Strategic Insights
      </Typography>

      {/* Key Metrics Row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '32px',
          maxWidth: '600px'
        }}
      >
        {/* Market Size */}
        {industry.marketSize && (
          <Box>
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '1.5px',
                opacity: 0.8,
                marginBottom: '8px'
              }}
            >
              MARKET SIZE
            </Typography>
            <Typography
              sx={{
                fontSize: '36px',
                fontWeight: 800,
                lineHeight: 1
              }}
            >
              ${industry.marketSize}
            </Typography>
          </Box>
        )}

        {/* Growth Rate */}
        {industry.growthRate && (
          <Box>
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '1.5px',
                opacity: 0.8,
                marginBottom: '8px'
              }}
            >
              ANNUAL GROWTH
            </Typography>
            <Typography
              sx={{
                fontSize: '36px',
                fontWeight: 800,
                lineHeight: 1
              }}
            >
              {industry.growthRate}%
            </Typography>
          </Box>
        )}

        {/* Investment Opportunities */}
        {industry.investmentOpportunities && (
          <Box>
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '1.5px',
                opacity: 0.8,
                marginBottom: '8px'
              }}
            >
              OPPORTUNITIES
            </Typography>
            <Typography
              sx={{
                fontSize: '36px',
                fontWeight: 800,
                lineHeight: 1
              }}
            >
              {industry.investmentOpportunities}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>

    {/* Footer Section */}
    <Box sx={{ position: 'relative', zIndex: 1 }}>
      <Box
        sx={{
          height: '1px',
          background: 'rgba(255,255,255,0.3)',
          marginBottom: '24px'
        }}
      />

      <Grid container spacing={3}>
        <Grid item xs={4}>
          <Typography sx={{ fontSize: '11px', fontWeight: 600, opacity: 0.8, marginBottom: '4px' }}>
            Report Date
          </Typography>
          <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>
            {reportDate.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </Typography>
        </Grid>

        <Grid item xs={4}>
          <Typography sx={{ fontSize: '11px', fontWeight: 600, opacity: 0.8, marginBottom: '4px' }}>
            Generated At
          </Typography>
          <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>
            {reportDate.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </Typography>
        </Grid>

        <Grid item xs={4}>
          <Typography sx={{ fontSize: '11px', fontWeight: 600, opacity: 0.8, marginBottom: '4px' }}>
            Platform
          </Typography>
          <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>
            VALUE CHAIN GLOBAL
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, marginTop: '12px' }}>
            VALUE CHAIN GLOBAL
          </Typography>
          <Typography sx={{ fontSize: '12px', opacity: 0.9, marginTop: '4px' }}>
            Industry Intelligence & Analysis Platform • Saudi Vision 2030
          </Typography>
          <Typography sx={{ fontSize: '11px', opacity: 0.7, marginTop: '8px' }}>
            www.value-chain.global
          </Typography>
        </Grid>
      </Grid>
    </Box>
  </Box>
);

// ============================================
// CONTENT PAGE WRAPPER COMPONENT
// ============================================
interface ContentPageProps {
  children: React.ReactNode;
  pageNumber: number;
  totalPages: number;
  industry: Industry;
  showHeader?: boolean;
}

const ContentPage: React.FC<ContentPageProps> = ({
  children,
  pageNumber,
  totalPages,
  industry,
  showHeader = true
}) => (
  <Box
    className="pdf-page content-page"
    sx={{
      width: '210mm',
      minHeight: '297mm',
      bgcolor: 'white',
      padding: '20mm',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      pageBreakAfter: 'always',
      '@media print': {
        pageBreakAfter: 'always'
      }
    }}
  >
    {/* Page Header */}
    {showHeader && (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingBottom: '16px',
          marginBottom: '24px',
          borderBottom: '3px solid transparent',
          borderImage: COLORS.brandGradientHorizontal + ' 1'
        }}
      >
        {/* Left: Logo & Industry */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img
            src={logoLight}
            alt="Logo"
            style={{ height: '32px' }}
          />
          <Box>
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 600,
                color: COLORS.gray600,
                letterSpacing: '0.5px'
              }}
            >
              INDUSTRY REPORT
            </Typography>
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 600,
                color: COLORS.gray900
              }}
            >
              {industry.name}
            </Typography>
          </Box>
        </Box>

        {/* Right: Date & Page Number */}
        <Box sx={{ textAlign: 'right' }}>
          <Typography
            sx={{
              fontSize: '10px',
              color: COLORS.gray600,
              marginBottom: '2px'
            }}
          >
            {new Date().toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}
          </Typography>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 600,
              color: COLORS.gray900
            }}
          >
            Page {pageNumber} of {totalPages}
          </Typography>
        </Box>
      </Box>
    )}

    {/* Main Content Area */}
    <Box sx={{ flex: 1, overflow: 'hidden' }}>
      {children}
    </Box>

    {/* Page Footer */}
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '16px',
        marginTop: '24px',
        borderTop: `1px solid ${COLORS.gray300}`
      }}
    >
      {/* Left: Copyright */}
      <Typography sx={{ fontSize: '10px', color: COLORS.gray600 }}>
        © {new Date().getFullYear()} VALUE CHAIN GLOBAL. All rights reserved.
      </Typography>

      {/* Right: Links */}
      <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <Typography sx={{ fontSize: '10px', color: COLORS.gray600 }}>
          www.value-chain.global
        </Typography>
        <Box sx={{ width: '1px', height: '12px', bgcolor: COLORS.gray300 }} />
        <Typography sx={{ fontSize: '10px', color: COLORS.gray600 }}>
          Saudi Vision 2030
        </Typography>
        <Box sx={{ width: '1px', height: '12px', bgcolor: COLORS.gray300 }} />
        <Typography sx={{ fontSize: '10px', color: COLORS.primaryBlue, fontWeight: 600 }}>
          CONFIDENTIAL
        </Typography>
      </Box>
    </Box>
  </Box>
);

// ============================================
// HISTORICAL GROWTH TIMELINE COMPONENT
// ============================================
interface HistoricalGrowthTimelineProps {
  data: Array<{ year: string; value: number }>;
}

const HistoricalGrowthTimeline: React.FC<HistoricalGrowthTimelineProps> = ({ data }) => (
  <Box sx={{ mb: 5 }}>
    <Typography variant="h5" sx={{ fontWeight: 700, mb: 4, color: COLORS.gray900 }}>
      Historical Growth Data
    </Typography>

    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative',
        paddingTop: '40px',
        paddingBottom: '40px',

        '&::before': {
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '10%',
          right: '10%',
          height: '3px',
          background: COLORS.brandGradientHorizontal,
          transform: 'translateY(-50%)',
          zIndex: 0
        }
      }}
    >
      {data.map((item, index) => (
        <Box
          key={item.year}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
            zIndex: 1,
            flex: 1
          }}
        >
          <Box
            sx={{
              width: '90px',
              height: '90px',
              borderRadius: '50%',
              bgcolor: 'white',
              border: `4px solid ${index === data.length - 1 ? COLORS.accentLime : COLORS.primaryBlue}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              marginBottom: '16px'
            }}
          >
            <Typography
              sx={{
                fontSize: '24px',
                fontWeight: 800,
                color: index === data.length - 1 ? COLORS.accentLime : COLORS.primaryBlue
              }}
            >
              {item.value}%
            </Typography>
          </Box>

          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: COLORS.gray700 }}>
            {item.year}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

// ============================================
// KPI CARDS COMPONENT
// ============================================
interface KPICardsProps {
  metrics: Array<{
    label: string;
    value: string | number;
    icon: React.ElementType;
    color: string;
  }>;
}

const KPICards: React.FC<KPICardsProps> = ({ metrics }) => (
  <Box sx={{ mb: 5 }}>
    <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: COLORS.gray900 }}>
      Key Performance Indicators
    </Typography>

    <Grid container spacing={3}>
      {metrics.map((kpi, index) => (
        <Grid item xs={12} md={4} key={index}>
          <Card
            elevation={1}
            sx={{
              padding: '24px',
              borderTop: `4px solid ${kpi.color}`,
              borderRadius: '8px',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 3
              }
            }}
          >
            <Box
              sx={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                bgcolor: `${kpi.color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}
            >
              <kpi.icon sx={{ color: kpi.color, fontSize: 32 }} />
            </Box>

            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                color: COLORS.gray600,
                letterSpacing: '1px',
                marginBottom: '8px'
              }}
            >
              {kpi.label.toUpperCase()}
            </Typography>

            <Typography sx={{ fontSize: '32px', fontWeight: 800, color: COLORS.gray900 }}>
              {kpi.value}
            </Typography>
          </Card>
        </Grid>
      ))}
    </Grid>
  </Box>
);

// ============================================
// MARKET TRENDS COMPONENT
// ============================================
interface MarketTrendsProps {
  trends: Array<{
    title: string;
    description: string;
    icon: React.ElementType;
    color: string;
    play?: string;
  }>;
}

const MarketTrends: React.FC<MarketTrendsProps> = ({ trends }) => (
  <Box sx={{ mb: 5 }}>
    <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: COLORS.gray900 }}>
      Market Trends
    </Typography>

    <Grid container spacing={3}>
      {trends.map((trend, index) => (
        <Grid item xs={12} md={6} key={index}>
          <Card
            elevation={2}
            sx={{
              height: '100%',
              borderTop: `4px solid ${trend.color}`,
              borderRadius: '8px',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4
              }
            }}
          >
            <CardContent sx={{ padding: '24px' }}>
              <Box sx={{ display: 'flex', gap: '16px', mb: 2 }}>
                <Box
                  sx={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '8px',
                    bgcolor: `${trend.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <trend.icon sx={{ color: trend.color, fontSize: 24 }} />
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: trend.color,
                    lineHeight: 1.3
                  }}
                >
                  {trend.title}
                </Typography>
              </Box>

              <Typography sx={{ fontSize: '14px', lineHeight: 1.6, color: COLORS.gray700 }}>
                {trend.description}
              </Typography>

              {trend.play && (
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${COLORS.gray200}` }}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: COLORS.gray600,
                      mb: 1
                    }}
                  >
                    PLAY:
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: COLORS.gray700, lineHeight: 1.5 }}>
                    {trend.play}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  </Box>
);

// ============================================
// SUPPORT ECOSYSTEM COMPONENT
// ============================================
interface SupportEcosystemProps {
  talent?: Record<string, any>;
  funding?: Record<string, any>;
  supplyChain?: Record<string, any>;
  keyPlayers?: string[];
}

const SupportEcosystem: React.FC<SupportEcosystemProps> = ({
  talent,
  funding,
  supplyChain,
  keyPlayers
}) => (
  <Box sx={{ mb: 5 }}>
    <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: COLORS.gray900 }}>
      Support Ecosystem
    </Typography>

    <Grid container spacing={3}>
      {/* Talent Development */}
      {talent && Object.keys(talent).length > 0 && (
        <Grid item xs={12} md={4}>
          <Card
            elevation={3}
            sx={{
              height: '100%',
              borderRadius: '8px',
              border: `2px solid ${COLORS.success}`,
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                background: `linear-gradient(135deg, ${COLORS.success}, ${COLORS.successLight})`,
                p: 2.5,
                color: 'white'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Talent Development
              </Typography>
            </Box>
            <CardContent sx={{ p: 2.5 }}>
              {Object.entries(talent).map(([key, value]) => (
                <Box key={key} sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: COLORS.gray600,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5
                    }}
                  >
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, color: COLORS.gray900 }}>
                    {String(value)}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Funding & Investment */}
      {funding && Object.keys(funding).length > 0 && (
        <Grid item xs={12} md={4}>
          <Card
            elevation={3}
            sx={{
              height: '100%',
              borderRadius: '8px',
              border: `2px solid ${COLORS.info}`,
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                background: `linear-gradient(135deg, ${COLORS.info}, ${COLORS.infoLight})`,
                p: 2.5,
                color: 'white'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Funding & Investment
              </Typography>
            </Box>
            <CardContent sx={{ p: 2.5 }}>
              {Object.entries(funding).map(([key, value]) => (
                <Box key={key} sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: COLORS.gray600,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5
                    }}
                  >
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, color: COLORS.gray900 }}>
                    {String(value)}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Supply Chain */}
      {supplyChain && Object.keys(supplyChain).length > 0 && (
        <Grid item xs={12} md={4}>
          <Card
            elevation={3}
            sx={{
              height: '100%',
              borderRadius: '8px',
              border: `2px solid ${COLORS.warning}`,
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                background: `linear-gradient(135deg, ${COLORS.warning}, ${COLORS.warningLight})`,
                p: 2.5,
                color: 'white'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Supply Chain
              </Typography>
            </Box>
            <CardContent sx={{ p: 2.5 }}>
              {Object.entries(supplyChain).map(([key, value]) => (
                <Box key={key} sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: COLORS.gray600,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5
                    }}
                  >
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, color: COLORS.gray900 }}>
                    {String(value)}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>

    {/* Key Players */}
    {keyPlayers && keyPlayers.length > 0 && (
      <Card elevation={2} sx={{ mt: 3, borderRadius: '8px', border: `1px solid ${COLORS.gray300}` }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.primaryBlue, mb: 2 }}>
            Key Support Organizations
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {keyPlayers.map((player, i) => (
              <Chip
                key={i}
                label={player}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  bgcolor: COLORS.gray100,
                  '&:hover': {
                    bgcolor: COLORS.gray200
                  }
                }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>
    )}
  </Box>
);

// ============================================
// MAIN COMPONENT
// ============================================
const IndustriesReportPageNew = () => {
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedIndustryIds, setSelectedIndustryIds] = useState<string[]>([]);
  const [reportDate] = useState(new Date());

  // Fetch industries
  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        setLoading(true);
        const data = await industryApi.getAllIndustries({
          includeSubIndustries: true,
          includeValueChain: true,
          includeSupportEcosystem: true
        });
        setIndustries(data);
        // Select first industry by default
        if (data.length > 0) {
          setSelectedIndustryIds([data[0].id]);
        }
      } catch (err) {
        setError('Failed to load industries');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchIndustries();
  }, []);

  // Get selected industries data
  const selectedIndustries = industries.filter(ind =>
    selectedIndustryIds.includes(ind.id)
  );

  // Handle industry selection toggle
  const handleToggleIndustry = (industryId: string) => {
    setSelectedIndustryIds(prev =>
      prev.includes(industryId)
        ? prev.filter(id => id !== industryId)
        : [...prev, industryId]
    );
  };

  // Value chain table preparation functions
  const prepareValueChainForPDF = () => {
    const tables = document.querySelectorAll('.value-chain-table-wrapper');

    tables.forEach(tableWrapper => {
      const element = tableWrapper as HTMLElement;
      const table = tableWrapper.querySelector('table');

      // Store original styles
      element.dataset.originalTransform = element.style.transform || '';
      element.dataset.originalWidth = element.style.width || '';

      // Apply scaling
      element.style.transform = 'scale(0.5)';
      element.style.transformOrigin = 'top left';
      element.style.width = '200%';
      element.style.overflow = 'visible';

      // Reduce font size
      if (table) {
        const tableElement = table as HTMLElement;
        tableElement.style.fontSize = '7px';
        tableElement.style.lineHeight = '1.2';

        const cells = table.querySelectorAll('th, td');
        cells.forEach(cell => {
          const cellElement = cell as HTMLElement;
          cellElement.style.padding = '4px 2px';
          cellElement.style.fontSize = '7px';
          cellElement.style.whiteSpace = 'normal';
          cellElement.style.wordBreak = 'break-word';
        });
      }
    });
  };

  const restoreValueChainAfterPDF = () => {
    const tables = document.querySelectorAll('.value-chain-table-wrapper');

    tables.forEach(tableWrapper => {
      const element = tableWrapper as HTMLElement;
      element.style.transform = element.dataset.originalTransform || '';
      element.style.width = element.dataset.originalWidth || '';

      const table = tableWrapper.querySelector('table');
      if (table) {
        const tableElement = table as HTMLElement;
        tableElement.style.fontSize = '';
        tableElement.style.lineHeight = '';

        const cells = table.querySelectorAll('th, td');
        cells.forEach(cell => {
          const cellElement = cell as HTMLElement;
          cellElement.style.padding = '';
          cellElement.style.fontSize = '';
          cellElement.style.whiteSpace = '';
          cellElement.style.wordBreak = '';
        });
      }
    });
  };

  // Calculate total pages for an industry
  const calculateTotalPages = (industry: Industry): number => {
    let pageCount = 2; // Cover + Executive Summary/Market Overview

    // Historical Growth & KPI Page
    pageCount += 1;

    // Market Trends Page
    pageCount += 1;

    // Support Ecosystem Page (conditional)
    if (industry.supportEcosystem?.talent || industry.supportEcosystem?.funding || industry.supportEcosystem?.supplyChain) {
      pageCount += 1;
    }

    // Value Chain Page (conditional)
    if (industry.valueChainStages && industry.valueChainStages.length > 0) {
      pageCount += 1;
    }

    return pageCount;
  };

  // Prepare gradients for PDF
  const prepareGradientsForPDF = () => {
    const pdfContainer = document.querySelector('.pdf-container');
    if (!pdfContainer) return;

    // Find all elements and check computed styles for gradients
    const allElements = pdfContainer.querySelectorAll('*');
    allElements.forEach((element) => {
      const el = element as HTMLElement;
      const computedStyle = window.getComputedStyle(el);
      const background = computedStyle.background || computedStyle.backgroundImage;

      // Check if element has a gradient
      if (background && (background.includes('linear-gradient') || background.includes('radial-gradient'))) {
        // Store original
        el.dataset.originalBackground = el.style.background || '';
        el.dataset.originalBackgroundImage = el.style.backgroundImage || '';

        // Replace with solid color - use primary blue
        el.style.setProperty('background', COLORS.primaryBlue, 'important');
        el.style.setProperty('background-image', 'none', 'important');
      }
    });
  };

  const restoreGradientsAfterPDF = () => {
    const pdfContainer = document.querySelector('.pdf-container');
    if (!pdfContainer) return;

    const modifiedElements = pdfContainer.querySelectorAll('[data-original-background]');
    modifiedElements.forEach((element) => {
      const el = element as HTMLElement;

      // Restore or remove the styles
      if (el.dataset.originalBackground) {
        if (el.dataset.originalBackground === '') {
          el.style.removeProperty('background');
        } else {
          el.style.background = el.dataset.originalBackground;
        }
      }

      if (el.dataset.originalBackgroundImage) {
        if (el.dataset.originalBackgroundImage === '') {
          el.style.removeProperty('background-image');
        } else {
          el.style.backgroundImage = el.dataset.originalBackgroundImage;
        }
      }

      delete el.dataset.originalBackground;
      delete el.dataset.originalBackgroundImage;
    });
  };

  // PDF Generation Function
  const handleGeneratePDF = async () => {
    if (selectedIndustries.length === 0) {
      alert('Please select at least one industry');
      return;
    }

    setIsGenerating(true);

    try {
      // Step 1: Prepare tables and gradients for PDF
      prepareValueChainForPDF();
      prepareGradientsForPDF();

      // Wait for DOM updates
      await new Promise(resolve => setTimeout(resolve, 200));

      // Step 2: Generate PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      const pages = document.querySelectorAll('.pdf-page');

      for (let i = 0; i < pages.length; i++) {
        const page = pages[i] as HTMLElement;

        // Capture with proper settings
        const canvas = await html2canvas(page, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#FFFFFF',
          width: page.scrollWidth,
          windowWidth: Math.max(page.scrollWidth, 1400),
          height: page.scrollHeight,
          scrollX: 0,
          scrollY: -window.scrollY,
          onclone: (clonedDoc) => {
            const clonedPage = clonedDoc.querySelector('.pdf-page');
            if (clonedPage) {
              (clonedPage as HTMLElement).style.transform = 'none';
            }
          }
        });

        // Add to PDF
        const imgData = canvas.toDataURL('image/jpeg', 0.92);
        const pdfWidth = 210;
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      }

      // Step 3: Restore original styles
      restoreValueChainAfterPDF();
      restoreGradientsAfterPDF();

      // Step 4: Save
      const fileName = `VCG_Industry_Report_${selectedIndustries[0].name.replace(/\s+/g, '_')}_${
        new Date().toISOString().split('T')[0]
      }.pdf`;

      pdf.save(fileName);
    } catch (error) {
      console.error('PDF generation failed:', error);
      console.error('Error details:', error instanceof Error ? error.message : String(error));
      restoreValueChainAfterPDF();
      restoreGradientsAfterPDF();
      alert(`Failed to generate PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsGenerating(false);
      setError(null);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading industries...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: COLORS.gray50 }}>
      {/* Control Panel */}
      <Box sx={{ bgcolor: 'white', borderBottom: `1px solid ${COLORS.gray200}`, py: 3 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
            Industry Report Generator
          </Typography>

          {/* Industry Selection */}
          <Paper elevation={0} sx={{ p: 3, bgcolor: COLORS.gray50, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Select Industries
            </Typography>
            <Grid container spacing={2}>
              {industries.map(industry => (
                <Grid item xs={12} sm={6} md={4} key={industry.id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedIndustryIds.includes(industry.id)}
                        onChange={() => handleToggleIndustry(industry.id)}
                      />
                    }
                    label={industry.name}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/* Generate Button */}
          <Button
            variant="contained"
            size="large"
            startIcon={isGenerating ? <CircularProgress size={20} /> : <PdfIcon />}
            onClick={handleGeneratePDF}
            disabled={isGenerating || selectedIndustries.length === 0}
            sx={{
              background: COLORS.brandGradient,
              px: 4,
              py: 1.5,
              fontSize: '16px',
              fontWeight: 600
            }}
          >
            {isGenerating ? 'Generating PDF...' : 'Generate PDF Report'}
          </Button>
        </Container>
      </Box>

      {/* PDF Preview Container */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 4 }}>
          Report Preview
        </Typography>

        {/* Hidden PDF Content - Will be captured */}
        <Box sx={{ display: 'none' }} className="pdf-container">
          {selectedIndustries.map((industry, industryIndex) => {
            const totalPages = calculateTotalPages(industry);
            const pages = {
              executiveSummary: 2,
              growthKPI: 3,
              marketTrends: 4,
              supportEcosystem: 5,
              valueChain: 6
            };

            return (
              <React.Fragment key={industry.id}>
                {/* Cover Page */}
                <CoverPage industry={industry} reportDate={reportDate} />

                {/* Content Pages */}
                <ContentPage
                  pageNumber={pages.executiveSummary}
                  totalPages={totalPages}
                  industry={industry}
                >
                {/* Executive Summary */}
                <Box sx={{ mb: 5 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      bgcolor: '#F8FBFF',
                      border: `1px solid ${COLORS.infoLight}`,
                      borderLeft: `4px solid ${COLORS.primaryBlue}`,
                      padding: '32px'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', mb: 3 }}>
                      <Box
                        sx={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '12px',
                          background: COLORS.brandGradient,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <AssessmentIcon sx={{ color: 'white', fontSize: 32 }} />
                      </Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: COLORS.primaryBlue }}>
                        Executive Summary
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '15px', lineHeight: 1.8, color: COLORS.gray700 }}>
                      {industry.summary || industry.description}
                    </Typography>
                  </Paper>
                </Box>

                {/* Market Overview Cards */}
                <Box sx={{ mb: 5 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                    Market Overview
                  </Typography>
                  <Grid container spacing={3}>
                    {/* Growth Rate */}
                    {industry.growthRate && (
                      <Grid item xs={12} md={6}>
                        <Card
                          elevation={2}
                          sx={{
                            height: '180px',
                            background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
                            border: '2px solid #81C784',
                            borderRadius: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: '24px'
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '11px',
                              fontWeight: 600,
                              letterSpacing: '1.5px',
                              color: '#2E7D32',
                              marginBottom: '12px'
                            }}
                          >
                            ANNUAL GROWTH RATE
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '56px',
                              fontWeight: 800,
                              color: '#2E7D32',
                              lineHeight: 1,
                              marginBottom: '12px'
                            }}
                          >
                            {industry.growthRate}%
                          </Typography>
                          <TrendingUpIcon sx={{ color: '#2E7D32', fontSize: 36 }} />
                        </Card>
                      </Grid>
                    )}

                    {/* Market Size */}
                    {industry.marketSize && (
                      <Grid item xs={12} md={6}>
                        <Card
                          elevation={2}
                          sx={{
                            height: '180px',
                            background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
                            border: '2px solid #64B5F6',
                            borderRadius: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: '24px'
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '11px',
                              fontWeight: 600,
                              letterSpacing: '1.5px',
                              color: '#1565C0',
                              marginBottom: '12px'
                            }}
                          >
                            MARKET SIZE
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '56px',
                              fontWeight: 800,
                              color: '#1565C0',
                              lineHeight: 1,
                              marginBottom: '12px'
                            }}
                          >
                            ${industry.marketSize}
                          </Typography>
                          <BusinessIcon sx={{ color: '#1565C0', fontSize: 36 }} />
                        </Card>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </ContentPage>

                {/* Historical Growth & KPI Page */}
                <ContentPage
                  pageNumber={pages.growthKPI}
                  totalPages={totalPages}
                  industry={industry}
                >
                  {/* Historical Growth Timeline */}
                  {industry.growthData && industry.growthData.length > 0 && (
                    <HistoricalGrowthTimeline data={industry.growthData} />
                  )}

                  {/* KPI Cards */}
                  <KPICards
                    metrics={[
                      {
                        label: 'Market Size',
                        value: industry.marketSize ? `$${industry.marketSize}` : 'N/A',
                        icon: BusinessIcon,
                        color: COLORS.primaryBlue
                      },
                      {
                        label: 'Growth Rate',
                        value: industry.growthRate ? `${industry.growthRate}%` : 'N/A',
                        icon: TrendingUpIcon,
                        color: COLORS.success
                      },
                      {
                        label: 'Opportunities',
                        value: industry.investmentOpportunities || 'N/A',
                        icon: AutoAwesomeIcon,
                        color: COLORS.accentLime
                      }
                    ]}
                  />
                </ContentPage>

                {/* Market Trends Page */}
                <ContentPage
                  pageNumber={pages.marketTrends}
                  totalPages={totalPages}
                  industry={industry}
                >
                  <MarketTrends
                    trends={[
                      {
                        title: 'Digital Transformation',
                        description: (industry.tabs as any)?.digitalTrends || 'The industry is experiencing rapid digital transformation with adoption of AI, IoT, and automation technologies driving operational efficiency and innovation.',
                        icon: AutoAwesomeIcon,
                        color: COLORS.primaryBlue,
                        play: 'Invest in digital infrastructure and talent development to capture market opportunities.'
                      },
                      {
                        title: 'Sustainability Focus',
                        description: (industry.tabs as any)?.sustainabilityTrends || 'Growing emphasis on ESG compliance and sustainable practices, driven by regulatory requirements and consumer demand.',
                        icon: AccountBalanceIcon,
                        color: COLORS.success,
                        play: 'Develop green technologies and sustainable supply chain solutions.'
                      },
                      {
                        title: 'Market Consolidation',
                        description: (industry.tabs as any)?.consolidationTrends || 'Industry consolidation through M&A activities creating larger players and driving operational synergies.',
                        icon: HandshakeIcon,
                        color: COLORS.warning,
                        play: 'Strategic partnerships and acquisitions to expand market reach.'
                      },
                      {
                        title: 'Talent Development',
                        description: (industry.tabs as any)?.talentTrends || 'Increasing focus on upskilling workforce and attracting top talent with specialized skills.',
                        icon: PeopleIcon,
                        color: COLORS.info,
                        play: 'Invest in training programs and create competitive compensation packages.'
                      }
                    ]}
                  />
                </ContentPage>

                {/* Support Ecosystem Page */}
                {(industry.supportEcosystem?.talent || industry.supportEcosystem?.funding || industry.supportEcosystem?.supplyChain) && (
                  <ContentPage
                    pageNumber={pages.supportEcosystem}
                    totalPages={totalPages}
                    industry={industry}
                  >
                    <SupportEcosystem
                      talent={industry.supportEcosystem?.talent}
                      funding={industry.supportEcosystem?.funding}
                      supplyChain={industry.supportEcosystem?.supplyChain}
                      keyPlayers={industry.supportEcosystem?.keyPlayers}
                    />
                  </ContentPage>
                )}

                {/* Value Chain Page */}
                {industry.valueChainStages && industry.valueChainStages.length > 0 && (
                  <ContentPage
                    pageNumber={pages.valueChain}
                    totalPages={totalPages}
                    industry={industry}
                  >
                  <Box sx={{ mb: 4 }}>
                    <Box
                      sx={{
                        background: COLORS.brandGradientHorizontal,
                        padding: '24px',
                        borderRadius: '8px 8px 0 0',
                        marginBottom: 0
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '24px',
                          fontWeight: 700,
                          color: 'white',
                          marginBottom: '4px'
                        }}
                      >
                        Value Chain Analysis
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: 'white', opacity: 0.95 }}>
                        Complete industry value chain mapping across all stages
                      </Typography>
                    </Box>

                    <Box
                      className="value-chain-table-wrapper"
                      sx={{
                        bgcolor: 'white',
                        border: `1px solid ${COLORS.gray200}`,
                        borderTop: 'none',
                        borderRadius: '0 0 8px 8px',
                        padding: '16px',
                        overflow: 'visible',
                        position: 'relative'
                      }}
                    >
                      <ValueChainTable
                        valueChainId={industry.id}
                        valueChainData={industry.valueChain || null}
                        industryName={industry.name}
                        industryId={industry.id}
                        subIndustryId={undefined}
                        subIndustries={[]}
                        onNavigate={() => {}}
                      />
                    </Box>
                    </Box>
                  </ContentPage>
                )}
              </React.Fragment>
            );
          })}
        </Box>

        {/* Visible Preview */}
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {selectedIndustries.length} {selectedIndustries.length === 1 ? 'Industry' : 'Industries'} Selected
          </Typography>
          <Typography color="text.secondary">
            Click "Generate PDF Report" to create your professional industry report
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default IndustriesReportPageNew;
